/**
 * W46 — Executive Cross-Cutting Dashboard.
 * Team activity + pipeline summary + forecast vs quota + account decay risk.
 * Tenant 1 only. No exports. No Recharts.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";
import { KpiTile } from "@/components/charts/svg/KpiTile";

interface ExecData {
  pipelineSummary: { totalOpen: number; totalWeighted: number; buckets: Record<string, number> };
  teamActivity: { totalActions: number; activeReps: number; totalReps: number };
  decayRisk: { count: number; totalValue: number };
  accountCount: number;
}

// R-3 fix (M-EXEC1): align to server canonical bucket keys (Wave G)
const BUCKET_ORDER = ["Commit", "Upside", "Best Case", "Pipeline", "Omit"] as const;
const BUCKET_COLORS: Record<string, string> = {
  Commit: "#15803D",
  Upside: "#EA580C",
  "Best Case": "#16A34A",
  Pipeline: "#0F766E",
  Omit: "#6B7280",
};

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export default function ExecutiveDashboard() {
  const session = getSession();
  const [data, setData] = useState<ExecData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }
    fetch("/api/dashboards/executive", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { console.error("[executive]", e); setError("Could not load executive data"); setLoading(false); });
  }, [session?.token]);

  const coverage = data && data.pipelineSummary.totalOpen > 0
    ? Math.round((data.pipelineSummary.totalWeighted / data.pipelineSummary.totalOpen) * 100)
    : null;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-[15px] font-semibold text-foreground">Executive View</h2>
      {data && !loading && (
        <p className="text-[12px] text-muted-foreground -mt-4">{data.accountCount} accounts</p>
      )}
      {error && <p className="text-[12px] text-[#DC2626]">{error}</p>}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiTile label="Total Open Pipeline" value={loading ? null : fmt(data?.pipelineSummary.totalOpen ?? 0)} colorVariant="teal" loading={loading} />
        <KpiTile label="Weighted Forecast" value={loading ? null : fmt(data?.pipelineSummary.totalWeighted ?? 0)} colorVariant="teal" loading={loading} />
        <KpiTile label="Pipeline Coverage" value={loading ? null : coverage != null ? `${coverage}%` : "\u2014"}
          colorVariant={coverage == null ? "default" : coverage >= 70 ? "green" : coverage >= 50 ? "yellow" : "red"}
          loading={loading} subValue="Weighted / open pipe" />
        <KpiTile label="Accounts in View" value={loading ? null : data?.accountCount ?? 0} loading={loading} />
      </div>

      {data && (
        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
          <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Pipeline by Forecast Bucket
          </h3>
          <div className="space-y-2">
            {BUCKET_ORDER.map((b) => {
              const val = data.pipelineSummary.buckets[b] ?? 0;
              const total = data.pipelineSummary.totalOpen || 1;
              const pct = Math.round((val / total) * 100);
              return (
                <div key={b} className="flex items-center gap-3 text-[12px]">
                  <span className="w-16 text-right font-semibold text-[11px] uppercase tracking-wide text-muted-foreground">{b}</span>
                  <div className="flex-1 h-3 bg-[#1F2937] rounded overflow-hidden">
                    <div className="h-full rounded transition-all"
                      style={{ width: `${pct}%`, backgroundColor: BUCKET_COLORS[b], opacity: b === "Omit" ? 0.7 : 1 }} />
                  </div>
                  <span className="w-16 tabular-nums text-foreground text-right">{fmt(val)}</span>
                  <span className="w-10 tabular-nums text-muted-foreground text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* R-2 fix: teamActivity is now an aggregate object, not an array */}
      {data && (
        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
          <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Team Activity
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <KpiTile label="Total actions (MTD)" value={data.teamActivity.totalActions} />
            <KpiTile label="Active reps" value={`${data.teamActivity.activeReps} of ${data.teamActivity.totalReps}`} />
          </div>
        </div>
      )}

      {/* R-2 fix: decayRisk is now an aggregate object, not an array */}
      {data && (
        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
          <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Account Decay Risk
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <KpiTile label="Decaying accounts" value={data.decayRisk.count} colorVariant={data.decayRisk.count > 0 ? "orange" : "default"} />
            <KpiTile label="At-risk value" value={fmt(data.decayRisk.totalValue)} colorVariant={data.decayRisk.totalValue > 0 ? "red" : "default"} />
          </div>
        </div>
      )}
    </div>
  );
}
